/**
 * Cuban Bikes Scoring Engine
 *
 * This module implements the scoring algorithm from the Excel workbook.
 * The algorithm calculates market share and profit for each team based on:
 * 1. Manufacturing decisions (with trade-offs)
 * 2. R&D upgrades (cumulative, no trade-offs)
 * 3. Segment matching (how well bike attributes match segment preferences)
 * 4. Advertising effectiveness
 * 5. Cross-team competition for market share
 */

import {
  ATTRIBUTES,
  SEGMENTS,
  SEGMENT_NAMES,
  TRADEOFFS,
  GLOBAL_PARAMS
} from './referenceData';

/**
 * Apply trade-offs to get the final manufactured bike attributes.
 * When a team selects high levels for certain attributes, other attributes get penalized.
 *
 * @param {Object} selectedAttributes - { Speed: 3, Comfort: 4, ... } selected levels (1-5)
 * @returns {Object} - Final bike attributes after trade-offs applied
 */
export function applyTradeoffs(selectedAttributes) {
  // Start with selected values
  const result = { ...selectedAttributes };

  // Apply trade-offs from each selected attribute
  for (const attr of ATTRIBUTES) {
    const level = selectedAttributes[attr];
    const tradeoffs = TRADEOFFS[attr]?.[level] || {};

    for (const [affectedAttr, penalty] of Object.entries(tradeoffs)) {
      result[affectedAttr] = (result[affectedAttr] || 0) + penalty;
    }
  }

  // Clamp all values to 1-5 range
  for (const attr of ATTRIBUTES) {
    result[attr] = Math.max(1, Math.min(5, result[attr]));
  }

  return result;
}

/**
 * Apply R&D upgrades cumulatively across rounds.
 * R&D upgrades improve attributes without trade-offs.
 *
 * @param {Object} manufacturedBike - Bike after trade-offs { Speed: 3, ... }
 * @param {Object[]} rndUpgradesPerRound - Array of { Speed: 0, Comfort: 1, ... } for each round
 * @param {number} upToRound - Calculate for this round (1, 2, or 3)
 * @returns {Object} - Final bike attributes after R&D upgrades
 */
export function applyRndUpgrades(manufacturedBike, rndUpgradesPerRound, upToRound) {
  const result = { ...manufacturedBike };

  // Sum R&D upgrades from round 1 through upToRound
  for (let r = 0; r < upToRound; r++) {
    const roundUpgrades = rndUpgradesPerRound[r] || {};
    for (const attr of ATTRIBUTES) {
      result[attr] = (result[attr] || 0) + (roundUpgrades[attr] || 0);
    }
  }

  // Clamp to 1-5 range
  for (const attr of ATTRIBUTES) {
    result[attr] = Math.max(1, Math.min(5, result[attr]));
  }

  return result;
}

/**
 * Calculate segment matching scores for a bike.
 * Counts how many attributes at level 5 or 4 match segment's high preferences (4 or 5).
 *
 * @param {Object} bike - Final bike attributes { Speed: 4, Comfort: 5, ... }
 * @returns {Object} - { segmentName: { matchOn5: n, matchOn4: m }, ... }
 */
export function calculateSegmentMatching(bike) {
  const results = {};

  for (const segmentName of SEGMENT_NAMES) {
    const prefs = SEGMENTS[segmentName].preferences;
    let matchOn5 = 0;
    let matchOn4 = 0;

    for (const attr of ATTRIBUTES) {
      const bikeLevel = bike[attr];
      const segmentPref = prefs[attr];
      const isHighPriority = segmentPref >= 4; // Segment wants this attribute

      if (isHighPriority) {
        if (bikeLevel === 5) {
          matchOn5++;
        } else if (bikeLevel === 4) {
          matchOn4++;
        }
      }
    }

    results[segmentName] = { matchOn5, matchOn4 };
  }

  return results;
}

/**
 * Calculate advertising effectiveness for a team for a specific round.
 * Considers performance ads (attribute-targeted) and brand ads (with carry-over).
 *
 * @param {Object} teamData - Team's data for all rounds
 * @param {number} round - Current round (1, 2, or 3)
 * @returns {Object} - { segmentName: effectivenessScore, ... }
 */
export function calculateAdvertisingEffectiveness(teamData, round) {
  const results = {};
  const { discountedImpact, brandSpendDistribution, brandSpendMultipliers, broadTargetsNoResidual } = GLOBAL_PARAMS;

  for (const segmentName of SEGMENT_NAMES) {
    const prefs = SEGMENTS[segmentName].preferences;
    let effectiveness = 0;

    // Performance ads for current round
    const currentRoundData = teamData.rounds[round - 1];
    const perfAds = currentRoundData?.advertising?.performance_ads || {};
    const targetedSegments = currentRoundData?.advertising?.segments || [];

    // Check if this segment was targeted
    const isTargeted = targetedSegments.includes(segmentName) ||
                       targetedSegments.includes('All Potential') ||
                       targetedSegments.includes('Broad');
    const isBroadTarget = targetedSegments.includes('All Potential') || targetedSegments.includes('Broad');

    if (isTargeted) {
      // Calculate performance ad effectiveness based on attribute alignment
      for (const attr of ATTRIBUTES) {
        const adSpend = perfAds[attr] || 0;
        const segmentPref = prefs[attr];
        // Ads on attributes the segment values (4 or 5) are more effective
        if (adSpend > 0 && segmentPref >= 4) {
          effectiveness += adSpend;
        }
      }

      // Broad targeting is less effective
      if (isBroadTarget) {
        effectiveness *= discountedImpact;
      }
    }

    // Brand ads with carry-over from previous rounds
    for (let spendRound = 1; spendRound <= round; spendRound++) {
      const roundData = teamData.rounds[spendRound - 1];
      const brandAds = roundData?.advertising?.brand_ads || {};
      const roundTargets = roundData?.advertising?.segments || [];
      const roundIsBroad = roundTargets.includes('All Potential') || roundTargets.includes('Broad');

      // Check if brand ads should carry over
      const distribution = brandSpendDistribution[spendRound]?.[round] || 0;
      const multiplier = brandSpendMultipliers[spendRound]?.[round] || 0;

      if (distribution > 0 && multiplier > 0) {
        // Broad brand ads don't have residual benefit
        if (roundIsBroad && broadTargetsNoResidual && spendRound < round) {
          continue;
        }

        // Sum brand ad spend on attributes the segment values
        for (const attr of ATTRIBUTES) {
          const brandSpend = brandAds[attr] || 0;
          const segmentPref = prefs[attr];
          if (brandSpend > 0 && segmentPref >= 4) {
            let brandEffect = brandSpend * distribution * multiplier;
            if (roundIsBroad) {
              brandEffect *= discountedImpact;
            }
            effectiveness += brandEffect;
          }
        }
      }
    }

    results[segmentName] = effectiveness;
  }

  return results;
}

/**
 * Calculate cross-team rankings and final units/profit for all teams for a round.
 *
 * @param {Object[]} allTeams - Array of { teamName, teamData } for all teams
 * @param {number} round - Current round (1, 2, or 3)
 * @returns {Object} - { teamName: { segmentResults: {...}, totalUnits, totalProfit }, ... }
 */
export function calculateRoundScores(allTeams, round) {
  // Step 1: Calculate each team's final bike and segment matching
  const teamBikes = {};
  const teamMatching = {};
  const teamAdEffectiveness = {};
  const teamProfitMargins = {};

  for (const { teamName, teamData } of allTeams) {
    // Get manufactured bike (from round 1 only - manufacturing is fixed)
    const mfgAttrs = teamData.rounds[0]?.manufacturing?.attributes || {};
    const manufacturedBike = applyTradeoffs(mfgAttrs);

    // Apply cumulative R&D upgrades
    const rndUpgrades = teamData.rounds.map(r => r?.rnd?.upgrades || {});
    const finalBike = applyRndUpgrades(manufacturedBike, rndUpgrades, round);
    teamBikes[teamName] = finalBike;

    // Calculate segment matching
    teamMatching[teamName] = calculateSegmentMatching(finalBike);

    // Calculate advertising effectiveness
    teamAdEffectiveness[teamName] = calculateAdvertisingEffectiveness(teamData, round);

    // Get profit margin for current round (Firestore stores these as strings)
    const roundData = teamData.rounds[round - 1];
    teamProfitMargins[teamName] = parseFloat(roundData?.profit_margin?.profit_margin_per_bike_dollars) || 0;
  }

  // Step 2: Calculate match share across all teams per segment
  const matchShares = {};
  for (const segmentName of SEGMENT_NAMES) {
    matchShares[segmentName] = {};

    // Sum total matches for this segment
    let totalMatch5 = 0;
    let totalMatch4 = 0;
    for (const { teamName } of allTeams) {
      const matching = teamMatching[teamName][segmentName];
      totalMatch5 += matching.matchOn5;
      totalMatch4 += matching.matchOn4;
    }

    // Calculate each team's share
    for (const { teamName } of allTeams) {
      const matching = teamMatching[teamName][segmentName];

      // Weighted sensitivity: match5 counts more than match4
      // Using weights from Excel: match5 * 1.0 + match4 * 0.5 (approximate)
      const weightedMatch = matching.matchOn5 + (matching.matchOn4 * 0.5);
      const totalWeighted = totalMatch5 + (totalMatch4 * 0.5);

      matchShares[segmentName][teamName] = totalWeighted > 0
        ? weightedMatch / totalWeighted
        : 1 / allTeams.length; // Equal share if no matches
    }
  }

  // Step 3: Apply advertising adjustment and calculate raw units
  const rawUnits = {};
  for (const segmentName of SEGMENT_NAMES) {
    rawUnits[segmentName] = {};

    let totalAdWeighted = 0;
    for (const { teamName } of allTeams) {
      const adEffect = teamAdEffectiveness[teamName][segmentName];
      const matchShare = matchShares[segmentName][teamName];
      // Combine match share with ad effectiveness
      const adWeighted = matchShare * (1 + adEffect / 100); // Ad effect scaled
      rawUnits[segmentName][teamName] = adWeighted;
      totalAdWeighted += adWeighted;
    }

    // Normalize to get share of segment
    for (const { teamName } of allTeams) {
      if (totalAdWeighted > 0) {
        rawUnits[segmentName][teamName] /= totalAdWeighted;
      } else {
        rawUnits[segmentName][teamName] = 1 / allTeams.length;
      }
    }
  }

  // Step 4: Apply population caps and calculate final units
  const results = {};
  for (const { teamName } of allTeams) {
    results[teamName] = {
      bike: teamBikes[teamName],
      matching: teamMatching[teamName],
      adEffectiveness: teamAdEffectiveness[teamName],
      segmentResults: {},
      totalUnits: 0,
      totalRevenue: 0,
      totalProfit: 0,
      profitMarginPerBike: teamProfitMargins[teamName]
    };
  }

  for (const segmentName of SEGMENT_NAMES) {
    const population = SEGMENTS[segmentName].population;

    // Calculate total allocated units for this segment
    let totalAllocated = 0;
    for (const { teamName } of allTeams) {
      totalAllocated += rawUnits[segmentName][teamName] * population;
    }

    // Apply population cap if needed
    const capRatio = totalAllocated > population ? population / totalAllocated : 1;

    for (const { teamName } of allTeams) {
      const share = rawUnits[segmentName][teamName];
      const units = Math.floor(share * population * capRatio);
      const profitMargin = teamProfitMargins[teamName];
      const profit = units * profitMargin;
      const roundData = allTeams.find(t => t.teamName === teamName).teamData.rounds[round - 1];
      const pricePerBike = parseFloat(roundData?.profit_margin?.price_per_bike_dollars) || 0;
      const revenue = units * pricePerBike;

      results[teamName].segmentResults[segmentName] = {
        share,
        units,
        revenue,
        profit
      };
      results[teamName].totalUnits += units;
      results[teamName].totalRevenue += revenue;
      results[teamName].totalProfit += profit;
    }
  }

  return results;
}

/**
 * Calculate full simulation results for all rounds.
 *
 * @param {Object[]} allTeams - Array of { teamName, teamData } for all teams
 * @returns {Object} - { round1: {...}, round2: {...}, round3: {...} }
 */
export function calculateSimulationResults(allTeams) {
  return {
    round1: calculateRoundScores(allTeams, 1),
    round2: calculateRoundScores(allTeams, 2),
    round3: calculateRoundScores(allTeams, 3)
  };
}
